import PersonIcon from "@mui/icons-material/Person";
import {
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	ListItem,
	ListItemText,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type React from "react";
import { useState } from "react";
import type { ParticipationStatus } from "@/types/event-attendance";
import { ParticipationToggle } from "./ParticipationToggle";
import type { UserInfo } from "./types";

interface UserRowProps {
	user: UserInfo;
	status: ParticipationStatus | undefined;
	pending: boolean;
	onStatusChange: (status: ParticipationStatus | null) => void;
}

export const UserRow: React.FC<UserRowProps> = ({
	user,
	status,
	pending,
	onStatusChange,
}) => {
	const [dialogOpen, setDialogOpen] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// Determine display name: show firstname + lastname if both present, otherwise show all info
	const displayName =
		user.firstname && user.lastname
			? `${user.firstname} ${user.lastname}`
			: `${user.firstname} ${user.lastname} (${user.displayname})`;

	const handleUserIconClick = () => {
		setDialogOpen(true);
	};

	return (
		<>
			<ListItem
				sx={{
					py: 1,
					px: 1.5,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 1,
					minHeight: "auto",
				}}
			>
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					sx={{ flex: 1, minWidth: 0 }}
				>
					<IconButton
						size="small"
						onClick={handleUserIconClick}
						sx={{ p: 0.5 }}
						title={isMobile ? "Klick für Details" : ""}
					>
						<PersonIcon fontSize="small" color="primary" />
					</IconButton>
					<ListItemText
						primary={displayName}
						primaryTypographyProps={{
							variant: "body2",
							sx: { overflow: "hidden", textOverflow: "ellipsis" },
						}}
						sx={{ m: 0 }}
					/>
				</Stack>
				<Box
					sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
				>
					{pending ? (
						<CircularProgress size={20} />
					) : (
						<ParticipationToggle
							value={status}
							onChange={onStatusChange}
							disabled={pending}
						/>
					)}
				</Box>
			</ListItem>

			{/* User details dialog */}
			<Dialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Spieler Details</DialogTitle>
				<DialogContent>
					<Stack spacing={1.5} sx={{ pt: 2 }}>
						<Box>
							<Typography variant="caption" color="textSecondary">
								Name
							</Typography>
							<Typography variant="body2">
								{user.firstname} {user.lastname}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" color="textSecondary">
								Anzeigename
							</Typography>
							<Typography variant="body2">{user.displayname}</Typography>
						</Box>
						<Box>
							<Typography variant="caption" color="textSecondary">
								E-Mail
							</Typography>
							<Typography variant="body2" sx={{ wordBreak: "break-all" }}>
								{user.email}
							</Typography>
						</Box>
						{user.mobile && (
							<Box>
								<Typography variant="caption" color="textSecondary">
									Telefon
								</Typography>
								<Typography variant="body2">{user.mobile}</Typography>
							</Box>
						)}
					</Stack>
				</DialogContent>
			</Dialog>
		</>
	);
};
